import React, { useEffect, useMemo } from "react";
import type { SetStateAction, Dispatch } from "react";
import { Button, Checkbox, DatePicker, Form, Modal, Select, Tooltip } from "antd";
import moment, { Moment } from "moment";
import MultipleSelect from "../../common/MultipleSelect";
import { Agreement } from "../../../lib/types/Agreement";
import { IProduct } from "../../../lib/types/IProduct";
import { LoadServiceData } from "../types/LoadServiceData";
import { DictionaryDto } from "../../../lib/interfaces";
import { selectFilterOptionWithChildren } from "../../../lib/utils/selectFilterOption";
import { displayFormat } from "../../../lib/utils/formatDate";
import { FormInfo } from "../../common/FormInfo/FormInfo";
import "./CreateChargeModal.less";

interface FormData {
  agreementId?: number;
  serviceIds?: number[];
  selectedStatuses?: number[];
  periodFrom: Moment | null;
  periodTo: Moment;
  includePastDates?: boolean;
}

interface ComponentProps {
  visible: boolean;
  onClose: () => void;
  handleCreateCharge: (values: FormData) => void;
  isServicesLoading: boolean;
  defaultSelectedStatuses: number[];
  setLoadServicesData: Dispatch<SetStateAction<LoadServiceData>>;
  agreements: Agreement[];
  agrStatus: DictionaryDto<number>[];
  serviceForAdjustmentCreate?: IProduct[];
  getTooltipTitle: (item: any) => JSX.Element;
}

export const CreateChargeModal = (props: ComponentProps) => {
  const {
    visible,
    onClose,
    handleCreateCharge,
    isServicesLoading,
    defaultSelectedStatuses,
    setLoadServicesData,
    agreements,
    agrStatus,
    serviceForAdjustmentCreate,
    getTooltipTitle,
  } = props;

  const [formCreateCharge] = Form.useForm<FormData>();

  const initialValues = useMemo<FormData>(() => {
    const lastDayPrevMonth = moment().subtract(1, "month").endOf("month");
    return {
      selectedStatuses: defaultSelectedStatuses,
      periodFrom: moment(lastDayPrevMonth).subtract(2, "month").add(1, "millisecond"),
      periodTo: lastDayPrevMonth,
      agreementId: agreements?.length === 1 ? agreements[0].id : undefined,
      serviceIds: [],
      includePastDates: false,
    };
  }, [defaultSelectedStatuses, agreements]);

  // Установка исходных значений формы по открытии модалки
  useEffect(() => {
    if (visible) {
      formCreateCharge.setFieldsValue(initialValues);
    } else {
      formCreateCharge.resetFields();
    }
  }, [visible, initialValues]);

  // Передача данных для загрузки услуг, при единственности договора
  useEffect(() => {
    if (visible && agreements?.length === 1) {
      setLoadServicesData((prev) => ({
        ...prev,
        agreementId: agreements[0].id,
      }));
    }
  }, [agreements, visible]);

  const disablePeriodTo = (date: Moment) => {
    const lastDayOfMonth = moment(date).endOf("month");
    return !moment(date).isSame(lastDayOfMonth, "day");
  };

  const onChangePeriodToCreateCharge = (date: Moment | null) => {
    if (!date) {
      return;
    }
    const periodFrom = formCreateCharge.getFieldValue("periodFrom");
    if (moment(periodFrom).isAfter(date)) {
      Modal.warning({
        title: "Начало расчетного периода позднее конца расчетного периода!",
        content: "Начало расчетного периода будет очищено",
        onOk: () => formCreateCharge.setFieldsValue({ periodFrom: null }),
        onCancel: () => formCreateCharge.setFieldsValue({ periodFrom: null }),
      });
    } else {
      const onConfirm = () => {
        const newPeriodFrom = moment(date).subtract(2, "month").add(1, "millisecond");
        formCreateCharge.setFieldsValue({ periodFrom: newPeriodFrom });
      };
      Modal.confirm({
        content: "Начало расчетного периода будет изменено. Вы хотите сохранить текущее значение?",
        okText: "Далее",
        okButtonProps: { style: { width: "108px" } },
        onOk: onConfirm,
        cancelText: "Сохранить",
        cancelButtonProps: { style: { width: "108px" } },
      });
    }
  };

  return (
    <Modal
      title="Создание начисления"
      className="create-charge-modal"
      visible={visible}
      onCancel={onClose}
      centered
      width={750}
      footer={
        <>
          <Button className="create-charge-modal__btn" onClick={onClose}>
            Отмена
          </Button>
          <Button
            className="create-charge-modal__btn"
            type="primary"
            onClick={formCreateCharge.submit}
            disabled={isServicesLoading}
          >
            Сохранить
          </Button>
        </>
      }
    >
      <Form
        form={formCreateCharge}
        autoComplete="off"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onFinish={handleCreateCharge}
        labelAlign="left"
        initialValues={initialValues}
      >
        <Form.Item noStyle dependencies={["selectedStatuses"]}>
          {({ getFieldValue }) => {
            const selectedStatuses = getFieldValue("selectedStatuses") || [];
            return (
              <Form.Item
                label="Номер договора"
                name="agreementId"
                rules={[{ required: true }]}
                className="form-item-required__hidden-error"
              >
                <Select
                  onChange={(agreementId: number) => {
                    setLoadServicesData((prev) => ({
                      ...prev,
                      agreementId,
                    }));
                    formCreateCharge.setFieldsValue({ serviceIds: undefined });
                  }}
                  loading={isServicesLoading}
                  disabled={isServicesLoading}
                  showSearch
                  filterOption={selectFilterOptionWithChildren}
                >
                  {agreements &&
                    agreements.length !== 0 &&
                    agreements
                      .filter(({ agreementStatusId }) => {
                        if (selectedStatuses.length === 0) {
                          return true;
                        }
                        return selectedStatuses.includes(agreementStatusId);
                      })
                      .map((item2) => (
                        <Select.Option key={item2.id} value={item2.id}>
                          {item2.agreementNumber}
                        </Select.Option>
                      ))}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item label="Статус договора" name="selectedStatuses">
          <MultipleSelect
            onChange={(values) => {
              const statuses = values.map(Number);
              const agreementId: number | undefined = formCreateCharge.getFieldValue("agreementId");
              const agreementStatusId = agreements.find(({ id }) => id === agreementId)
                ?.agreementStatusId;
              if (statuses.length && agreementStatusId && !statuses.includes(agreementStatusId)) {
                formCreateCharge.setFieldsValue({ agreementId: undefined, serviceIds: [] });
                setLoadServicesData((prev) => ({
                  ...prev,
                  agreementId: undefined,
                }));
              }
            }}
            allValues={agrStatus?.map(({ id }) => id) || []}
            placeholder="Статус"
            style={{ height: "30px" }}
            allowClear
            maxTagCount={1}
            showSearch
            filterOption={selectFilterOptionWithChildren}
            disabled={isServicesLoading}
          >
            {[...agrStatus]
              .sort((a, b) => (a.id > b.id ? 1 : -1))
              .map(({ id, name }) => (
                <Select.Option key={id} value={id}>
                  {name}
                </Select.Option>
              ))}
          </MultipleSelect>
        </Form.Item>
        <Form.Item noStyle dependencies={["agreementId"]}>
          {({ getFieldValue }) => {
            const agreementId: number | undefined = getFieldValue("agreementId");
            const placeholder = (() => {
              if (agreementId) {
                return isServicesLoading ? "Идет загрузка продуктов..." : undefined;
              }
              return "Необходимо выбрать договор";
            })();
            return (
              <Form.Item
                label="Продукт"
                name="serviceIds"
                rules={[{ required: true }]}
                className="form-item-required__hidden-error"
              >
                <Select
                  allowClear
                  loading={isServicesLoading}
                  filterOption={selectFilterOptionWithChildren}
                  mode="multiple"
                  showSearch
                  maxTagCount={2}
                  disabled={!agreementId || isServicesLoading}
                  placeholder={placeholder}
                >
                  {serviceForAdjustmentCreate &&
                    serviceForAdjustmentCreate
                      .filter((item) => item.serviceStatusId !== 5)
                      .map((item, idx) => {
                        const name = `${item.id} - ${item?.serviceType?.name}${
                          item?.offering?.extName ? ` - ${item?.offering?.extName}` : ""
                        }`;
                        return (
                          <Select.Option key={idx} value={item?.id}>
                            <Tooltip
                              overlayInnerStyle={{
                                display: "inline-block",
                                whiteSpace: "nowrap",
                                padding: "0",
                              }}
                              title={getTooltipTitle(item)}
                              placement="topLeft"
                            >
                              {name}
                            </Tooltip>
                          </Select.Option>
                        );
                      })}
                </Select>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item noStyle dependencies={["agreementId"]}>
          {({ getFieldValue }) => {
            const agreementId: number | undefined = getFieldValue("agreementId");
            return (
              <Form.Item name="includePastDates" valuePropName="checked">
                <Checkbox
                  onChange={(event) => {
                    const includePastDates = event.target.checked;
                    setLoadServicesData((prev) => ({
                      ...prev,
                      includePastDates,
                    }));
                    formCreateCharge.setFieldsValue({ serviceIds: [] });
                  }}
                  disabled={!agreementId || isServicesLoading}
                >
                  Только закрытые
                </Checkbox>
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item dependencies={["periodTo"]} noStyle>
          {({ getFieldValue }) => {
            const periodTo = getFieldValue("periodTo");
            const disablePeriodFrom = (date: Moment) => date.isAfter(periodTo);
            return (
              <Form.Item
                label={
                  <>
                    Начало расчетного периода <br />
                    (отбор продуктов):
                  </>
                }
                colon={false}
                wrapperCol={{ span: 12 }}
                name="periodFrom"
                className="form-item-required__hidden-error"
              >
                <DatePicker format={displayFormat} disabledDate={disablePeriodFrom} />
              </Form.Item>
            );
          }}
        </Form.Item>
        <Form.Item
          label="Конец расчетного периода"
          wrapperCol={{ span: 12 }}
          name="periodTo"
          rules={[{ required: true }]}
          className="form-item-required__hidden-error"
        >
          <DatePicker
            format={displayFormat}
            disabledDate={disablePeriodTo}
            onChange={onChangePeriodToCreateCharge}
            allowClear={false}
          />
        </Form.Item>
      </Form>
      <span style={{ color: "rgba(255, 69, 0, 0.59)" }}>
        Не рекомендуется создавать внеочередные начисления за транзакционные продукты, включающие
        правила назначения цены с типом <b>Цена за транзакцию</b>
      </span>
      <FormInfo getFormValues={() => formCreateCharge.getFieldsValue()} />
    </Modal>
  );
};
